import { Component, ElementRef, inject, OnInit, viewChild } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { LayoutService } from './service/app.layout.service';
import { AuthService } from '@core/services/auth.services';
import { Store } from '@ngrx/store';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LogisticsServiceProviderBrandingService } from '@core/services/logistics-service-provider-branding.service';
import { selectEnvironment } from '../shared/store/app';
import { UserPreferencesService } from '@core/services/user-preferences.service';
@Component({
  selector: 'app-topbar',
  standalone: false,
  templateUrl: './app.topbar.component.html',
  styleUrls: ['./app.topbar.component.css']
})
export class AppTopBarComponent {
  public layoutService = inject(LayoutService);
  private authService = inject(AuthService);
  private store = inject(Store);

  /** Mirrors the user menu popover, for the avatar button's aria-expanded. */
  userMenuOpen = false;

  private readonly providerBranding = inject(LogisticsServiceProviderBrandingService);
  private readonly userPreferences = inject(UserPreferencesService);

  /**
   * Collapsed, the 7rem rail has no room for a lockup, so it shows the owner's favicon (white in light mode): it is
   * drawn for small sizes, and the white silhouette keeps it readable on the menu colour, so no second upload is
   * needed for light and dark (Dan, 2026-10-16). Without a favicon the logo stays, scaled to the rail.
   */
  get railMarkHasBackground(): boolean {
    return this.providerBranding.faviconHasBackground();
  }

  get railMark(): string | null {
    return this.userPreferences.menuCollapsed() ? this.providerBranding.faviconUrl() : null;
  }

  menu: MenuItem[] = [];
  environment: string = '';
  // v5 premium: the marketing-site lockup (white mark + green wordmark), which is
  // built for dark chrome and needs no plate or recolour filter. Same asset as
  // providencesolutions.io, so the app and the site read as one brand.
  defaultLogo = 'assets/images/logo-providence-full.webp';
  logo = this.defaultLogo;

  /**
   * The resolved owner's own logo, when one has been uploaded, preferring the variant that reads against the
   * current chrome. Falls back to the Providence lockup, so an owner without artwork is unaffected.
   */
  get displayLogo(): string {
    const dark = this.providerBranding.logoDarkUrl();
    const light = this.providerBranding.logoUrl();
    const preferred = this.colorScheme === 'dark' ? (dark ?? light) : (light ?? dark);
    return preferred ?? this.logo;
  }
  menuButton = viewChild.required<ElementRef>('menubutton');

  constructor() {
    this.store
      .select(selectEnvironment)
      .pipe(takeUntilDestroyed())
      .subscribe((data) => {
        // Same lockup in every environment; the environment itself is still
        // signalled by the footer build string and the login screen heading.
        this.environment = (data ?? '').toLowerCase();
        this.logo = this.defaultLogo;
      });
  }

  onMenuButtonClick() {
    this.layoutService.onMenuToggle();
  }

  get layoutTheme(): string {
    return this.layoutService.config.layoutTheme;
  }

  get colorScheme(): string {
    return this.layoutService.config.colorScheme;
  }

  getUserDetail(): { firstInitial: string; lastInitial: string } | null {
    const info = this.authService.currentUser();
    if (info == null) return null;
    return {
      firstInitial: info.firstName ? info.firstName.charAt(0) : '',
      lastInitial: info.lastName ? info.lastName.charAt(0) : ''
    };
  }
  get userInfo(): string {
    const userInfo = this.authService.currentUser();
    if (userInfo) {
      return `${userInfo.firstName} ${userInfo.lastName}`;
    }
    return '';
  }
  get userRole(): string {
    const userInfo = this.authService.currentUser();
    if (userInfo) {
      return userInfo.roleDisplayName;
    }
    return '';
  }
}
